"use client";

import { useEffect, useSyncExternalStore } from "react";
import { getGtmConsent, subscribeToGtmConsent } from "./gtmConsent";
import { pushToDataLayer } from "./pushToDataLayer";

type GoogleTagManagerProps = {
  nonce?: string;
};

const GTM_SCRIPT_ID = "gtm-script";

/**
 * Google Tag Manager component.
 * Only loads GTM if marketing/analytics consent is given via Usercentrics.
 *
 * Injects the GTM bootstrap imperatively in a useEffect so the script
 * also executes when consent is granted after the initial page load.
 */
export const GoogleTagManager = ({
  nonce: initialNonce,
}: GoogleTagManagerProps) => {
  const consent = useSyncExternalStore(
    subscribeToGtmConsent,
    getGtmConsent,
    () => null
  );

  useEffect(() => {
    if (consent === false && document.getElementById(GTM_SCRIPT_ID)) {
      window.location.reload();
      return;
    }

    if (consent !== true) return;

    const measurementUrl = process.env.NEXT_PUBLIC_GTM_MEASUREMENT_URL;
    const config = process.env.NEXT_PUBLIC_GTM_CONFIG;

    if (!measurementUrl || !config) return;
    if (document.getElementById(GTM_SCRIPT_ID)) return;

    const nonce =
      initialNonce ||
      document
        .querySelector('meta[name="csp-nonce"]')
        ?.getAttribute("content") ||
      undefined;

    pushToDataLayer({ event: "gtm.js", "gtm.start": Date.now() });
    pushToDataLayer({
      event: "page_view",
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });

    const script = document.createElement("script");
    script.id = GTM_SCRIPT_ID;
    script.async = true;
    script.src = `${measurementUrl}?${config}`;
    if (nonce) script.nonce = nonce;

    document.head.appendChild(script);
  }, [consent, initialNonce]);

  return null;
};
